/*
 * useApi.js
 * -----------------------------------------------------------------------------
 * Frontend API client wrapper used across the Rage Room scheduler UI.
 *
 * Responsibilities:
 * - Centralizes base configuration for API requests (base URL, headers, etc.).
 * - Provides shared helpers such as query-string parsing.
 * - Wires feature-specific controllers (e.g., rageRoom) to the shared base client.
 *
 * Notes:
 * - Automatically attaches the x-app-id header so the backend can identify the app.
 * - Serializes request bodies to JSON unless a FormData instance or raw string is provided.
 * - Exposes the underlying baseApiCall for advanced use cases.
 */

import createRageRoomController from "./controllers/rageRoomController";

const useApi = () => {
	const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api";
	const appId = process.env.REACT_APP_APPID || "default-app-id";

	/**
	 * Wrapper function that replaces the fetch call.
	 * Override this to plug in alternate HTTP clients if needed.
	 *
	 * @param {string} url - Fully qualified API endpoint.
	 * @param {RequestInit} options - Request options.
	 * @returns {Promise<Response>} Raw fetch response.
	 */
	const useApiCall = async (url, options) => {
		return await fetch(url, options);
	};

	/**
	 * Executes an API request against the configured backend.
	 *
	 * @param {string} endpoint - API endpoint that begins with a slash.
	 * @param {RequestInit & { body?: any }} [options={}] - Fetch options.
	 * @returns {Promise<any>} Parsed JSON payload (or null for 204 responses).
	 * @throws {Error} When the request fails or the server returns a non-OK status.
	 */
	const baseApiCall = async (endpoint, options = {}) => {
		const url = `${baseUrl}${endpoint}`;
		const isFormData = options.body instanceof FormData;
		const isStringBody = typeof options.body === "string";

		const headers = {
			"x-app-id": appId,
			...options.headers
		};

		if (options.body && !isFormData && !isStringBody && !headers["Content-Type"]) {
			headers["Content-Type"] = "application/json";
		}

		const finalOptions = {
			credentials: "include",
			method: options.method || "GET",
			...options,
			headers,
			body: options.body && !isFormData && !isStringBody ? JSON.stringify(options.body) : options.body
		};

		try {
			const response = await useApiCall(url, finalOptions);
			if (!response.ok) {
				const errorText = await response.json();
				throw new Error(errorText.error.message);
			}
			if (response.status === 204) {
				return null;
			}
			return await response.json();
		} catch (error) {
			console.error("API call failed:", error);
			throw error;
		}
	};

	/**
	 * Converts an object of query parameters into a serialized query string.
	 *
	 * @param {Record<string, string | number | boolean | undefined | null>} [queryParams={}] - Raw query params.
	 * @returns {string} Serialized query string (including leading "?") or an empty string.
	 */
	const parseQueryParams = (queryParams = {}) => {
		const entries = Object.entries(queryParams).filter(
			([, value]) => value !== undefined && value !== null && value !== ""
		);
		return entries.length ? `?${new URLSearchParams(entries).toString()}` : "";
	};

	// Instantiate feature controllers with shared dependencies.
	const rageRoom = createRageRoomController({ baseApiCall, parseQueryParams });

	return {
		/**
		 * Low-level request helper for advanced/edge use cases.
		 */
		baseApiCall,

		/**
		 * Rage Room API controller.
		 * Surfaces admin and public endpoints related to rage room management.
		 */
		rageRoom
	};
};

export default useApi;